// Ask AI's inline answer (the bar above each grid) as the reader should see it: the model writes a
// little markdown — **bold**, *italic*, `code`, "- " lists, "# " headings — and the bar shows it, never
// the asterisks. Only these; anything else stays text. React elements, never HTML from the answer.

import type { ReactNode } from "react";

/** A run of text and how it shows. */
export interface MarkdownPart {
  text: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
}

/** A line of the answer: a paragraph line or a list item, its parts. */
export interface MarkdownLine {
  kind: "text" | "item";
  parts: MarkdownPart[];
}

// `code`, **bold** / __bold__, *italic* (a star not followed by a space: "5 * 3" stays as it is).
const INLINE = /(`[^`\n]+`|\*\*[^*\n]+?\*\*|__[^_\n]+?__|\*[^*\s][^*\n]*?\*)/g;

export function inlineParts(line: string): MarkdownPart[] {
  const parts: MarkdownPart[] = [];
  let at = 0;
  for (const m of line.matchAll(INLINE)) {
    const token = m[0];
    if (m.index > at) parts.push({ text: line.slice(at, m.index) });
    if (token.startsWith("`")) parts.push({ text: token.slice(1, -1), code: true });
    else if (token.startsWith("**") || token.startsWith("__")) parts.push({ text: token.slice(2, -2), bold: true });
    else parts.push({ text: token.slice(1, -1), italic: true });
    at = m.index + token.length;
  }
  if (at < line.length) parts.push({ text: line.slice(at) });
  return parts;
}

/** The answer's lines: "- " and "* " (and "1. ") are list items; "# " heading marks go. */
export function markdownLines(text: string): MarkdownLine[] {
  return text.split("\n").map((raw) => {
    const item = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/.exec(raw);
    const line = item ? item[1]! : raw.replace(/^\s*#{1,6}\s+/, "");
    return { kind: item ? "item" : "text", parts: inlineParts(line) };
  });
}

function Part({ part }: { part: MarkdownPart }) {
  if (part.code) return <code className="bg-muted rounded px-1 font-mono text-[0.92em]">{part.text}</code>;
  if (part.bold) return <strong className="font-semibold">{part.text}</strong>;
  if (part.italic) return <em>{part.text}</em>;
  return <>{part.text}</>;
}

/** The answer, its markdown shown: lines as lines, list items with a bullet. */
export function InlineMarkdown({ text }: { text: string }): ReactNode {
  return markdownLines(text).map((line, i) =>
    line.kind === "item" ? (
      <span key={i} className="flex gap-1.5 pl-1">
        <span aria-hidden>•</span>
        <span>
          {line.parts.map((p, j) => (
            <Part key={j} part={p} />
          ))}
        </span>
      </span>
    ) : (
      <span key={i} className="block min-h-[1em]">
        {line.parts.map((p, j) => (
          <Part key={j} part={p} />
        ))}
      </span>
    ),
  );
}
